import React, { useState } from 'react';
import type { LoyaltyReward, AppOffer, AppOfferType, Page, Customer, MenuItem, Category } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import RewardModal from '../components/loyalty/RewardModal';
import MealPlansTab from '../components/loyalty/MealPlansTab';

interface LoyaltyPageProps {
  rewards: LoyaltyReward[];
  onSave: (reward: LoyaltyReward) => void | Promise<void>;
  onDelete: (rewardId: number) => void | Promise<void>;
  offers: AppOffer[];
  onSaveOffer: (offer: AppOffer) => void | Promise<void>;
  onDeleteOffer: (offerId: string) => void | Promise<void>;
  customers: Customer[];
  menuItems: MenuItem[];
  categories: Category[];
  currentStaffId?: number | string;
  currentStaffName?: string;
  isManager?: boolean;
  onNavigate?: (page: Page) => void;
  /** Settings → Meal plans. Hidden until that switch is on. */
  mealPlansEnabled?: boolean;
}

const emptyOffer = (): AppOffer => ({
  id: '',
  title: '',
  description: '',
  type: 'fixed_discount',
  value: 5,
  minSpend: null,
  startAt: null,
  endAt: null,
  isActive: true,
  maxClaimsPerUser: 1,
  totalClaimLimit: null,
});

const LoyaltyPage: React.FC<LoyaltyPageProps> = ({
  rewards,
  onSave,
  onDelete,
  offers,
  onSaveOffer,
  onDeleteOffer,
  customers,
  menuItems,
  categories,
  currentStaffId,
  currentStaffName,
  isManager = true,
  onNavigate,
  mealPlansEnabled = false,
}) => {
  const [tab, setTab] = useState<'rewards' | 'offers' | 'meal-plans'>('rewards');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<LoyaltyReward | null>(null);
  const [editingOffer, setEditingOffer] = useState<AppOffer | null>(null);
  const [offerDraft, setOfferDraft] = useState<AppOffer>(emptyOffer());

  const handleOpenModal = (reward: LoyaltyReward | null = null) => {
    setEditingReward(reward);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingReward(null);
  };

  const handleSave = async (reward: LoyaltyReward) => {
    await onSave(reward);
    handleCloseModal();
  };

  const handleDelete = async (rewardId: number) => {
    if (window.confirm('Are you sure you want to delete this reward?')) {
      await onDelete(rewardId);
    }
  };

  const openOfferEditor = (offer: AppOffer | null) => {
    setEditingOffer(offer);
    setOfferDraft(offer ? { ...offer } : emptyOffer());
  };

  const saveOfferDraft = async () => {
    if (!offerDraft.title.trim()) {
      alert('Offer title is required.');
      return;
    }
    await onSaveOffer({
      ...offerDraft,
      id: offerDraft.id || `offer-${Date.now()}`,
      title: offerDraft.title.trim(),
    });
    setEditingOffer(null);
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Loyalty &amp; App Offers</h1>
              <p className="text-sm text-gray-500 mt-1">
                Staff manage catalogs here. The customer app syncs from POS — do not use mobile admin-web.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {onNavigate && (
                <>
                  <button
                    type="button"
                    onClick={() => onNavigate('customer-app-push')}
                    className="text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    App push
                  </button>
                  <button
                    type="button"
                    onClick={() => onNavigate('customer-app-feedback')}
                    className="text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    App feedback
                  </button>
                  <button
                    type="button"
                    onClick={() => onNavigate('customer-app-chat')}
                    className="text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    App chat
                  </button>
                  <button
                    type="button"
                    onClick={() => onNavigate('catering')}
                    className="text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Catering
                  </button>
                  <button
                    type="button"
                    onClick={() => onNavigate('customer-app-announcements')}
                    className="text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Announcements
                  </button>
                </>
              )}
              {tab === 'rewards' ? (
                <button
                  type="button"
                  onClick={() => handleOpenModal()}
                  className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add reward
                </button>
              ) : tab === 'offers' ? (
                <button
                  type="button"
                  onClick={() => openOfferEditor(null)}
                  className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add app offer
                </button>
              ) : null}
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => setTab('rewards')}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold ${
                tab === 'rewards' ? 'bg-pink-100 text-pink-800' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Point rewards
            </button>
            <button
              type="button"
              onClick={() => setTab('offers')}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold ${
                tab === 'offers' ? 'bg-pink-100 text-pink-800' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              App offers
            </button>
            {mealPlansEnabled && (
              <button
                type="button"
                onClick={() => setTab('meal-plans')}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold ${
                  tab === 'meal-plans' ? 'bg-pink-100 text-pink-800' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Meal plans
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        {tab === 'rewards' ? (
          <div className="bg-white shadow rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reward Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points Required</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                  <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rewards.map((reward) => (
                  <tr key={reward.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{reward.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{reward.pointsRequired}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">${reward.discountValue.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{reward.isActive === false ? 'No' : 'Yes'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button type="button" onClick={() => handleOpenModal(reward)} className="text-pink-600 hover:text-pink-900 mr-4">
                        <EditIcon className="w-5 h-5" />
                      </button>
                      <button type="button" onClick={() => void handleDelete(reward.id)} className="text-red-600 hover:text-red-900">
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === 'offers' ? (
          <div className="bg-white shadow rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                  <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {offers.map((offer) => (
                  <tr key={offer.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{offer.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{offer.type}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{offer.value}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{offer.isActive ? 'Yes' : 'No'}</td>
                    <td className="px-6 py-4 text-right text-sm">
                      <button type="button" onClick={() => openOfferEditor(offer)} className="text-pink-600 mr-4">
                        <EditIcon className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Delete this offer?')) void onDeleteOffer(offer.id);
                        }}
                        className="text-red-600"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {offers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500 text-sm">
                      No app offers yet. Add one — it syncs to the customer app within ~15 minutes.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : mealPlansEnabled ? (
          <MealPlansTab
            customers={customers}
            menuItems={menuItems}
            categories={categories}
            currentStaffId={currentStaffId}
            currentStaffName={currentStaffName}
            isManager={isManager}
          />
        ) : null}
      </main>
      {isModalOpen && (
        <RewardModal reward={editingReward} onClose={handleCloseModal} onSave={(r) => void handleSave(r)} />
      )}
      {editingOffer !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-3">
            <h2 className="text-lg font-bold text-gray-800">{offerDraft.id ? 'Edit offer' : 'New app offer'}</h2>
            <label className="block text-sm">
              <span className="text-gray-600">Title</span>
              <input
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={offerDraft.title}
                onChange={(e) => setOfferDraft((p) => ({ ...p, title: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-600">Description</span>
              <textarea
                className="mt-1 w-full border rounded-md px-3 py-2"
                rows={3}
                value={offerDraft.description}
                onChange={(e) => setOfferDraft((p) => ({ ...p, description: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-600">Type</span>
              <select
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={offerDraft.type}
                onChange={(e) => setOfferDraft((p) => ({ ...p, type: e.target.value as AppOfferType }))}
              >
                <option value="fixed_discount">Fixed discount ($)</option>
                <option value="percent_discount">Percent discount</option>
                <option value="free_item">Free item</option>
                <option value="bonus_points">Bonus points</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-gray-600">Value</span>
              <input
                type="number"
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={offerDraft.value}
                onChange={(e) => setOfferDraft((p) => ({ ...p, value: Number(e.target.value) || 0 }))}
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={offerDraft.isActive}
                onChange={(e) => setOfferDraft((p) => ({ ...p, isActive: e.target.checked }))}
              />
              Active (visible in app when synced)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-4 py-2 rounded-md border" onClick={() => setEditingOffer(null)}>
                Cancel
              </button>
              <button type="button" className="px-4 py-2 rounded-md bg-pink-500 text-white" onClick={() => void saveOfferDraft()}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyPage;
